import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Tab,
    Tabs,
    TextField
} from '@material-ui/core';
import { startCase } from 'lodash';
import * as moment from 'moment';
import * as React from 'react';
import { SearchProfileSection, searchSectionName } from 'shared/models/search';

import { css } from '@emotion/core';
import { Alert } from '@material-ui/lab';
import { getSimilaritySearchResults, getSimilaritySkillCounts } from '../api';
import { Spinner } from '../core-ui/spinner';
import { CellValue, DataPanel, DataPanelColumn } from './data-panel';
import { CompanyNameSelectField } from './talent-intelligence/company-name-select';

type SearchType = 'skills' | 'companies';
type ResultColumn = 'result' | 'similarity' | 'count' | 'action';

interface SearchResult {
    result: string;
    similarity?: number;
    count?: number;
    url: string;
}

interface DefaultProps {
    searchType: SearchType;
    searchTypes: SearchType[];
    searchText: string;
    onClose: () => void;
    buttonForValue?: (value: { name: string; url: string }) => JSX.Element;
    dataSource: Array<{ label: string; value: string }>;
    searchIn: SearchProfileSection;
}

export interface SimilaritySearchProps extends Partial<DefaultProps> {}

interface SimilaritySearchState {
    searchType: SearchType;
    searchText: string;
    company: { name: string; url: string };
    topn: any;
    loadingResults: boolean;
    countsError: Error | null;
    searchResults: SearchResult[];
}

const styles = css`
    .MuiDialogTitle-root {
        padding: 0;
    }

    .MuiTableCell-root {
        padding: 8px 16px;
    }

    .dialog-content {
        padding: 0;
        width: 720px;

        .similarity-search-form {
            display: flex;
            align-items: center;
            padding: 16px;

            .similarity-search-topn {
                flex: 0 0 auto;
                width: 100px;
                margin-right: 20px;
            }

            .company-name-select {
                flex: 1 1 auto;
            }
        }

        .similarity-search-results {
            padding: 16px;
        }
    }
`;

export class SimilaritySearch extends React.Component<SimilaritySearchProps, SimilaritySearchState> {
    static defaultProps: DefaultProps = {
        buttonForValue: null,
        dataSource: [],
        onClose: () => null,
        searchIn: 'last3YearAndSummary',
        searchText: '',
        searchType: 'skills',
        searchTypes: ['skills', 'companies']
    };

    resultCellRenderers: { [K in ResultColumn]?: (value: CellValue, row: SearchResult) => string | JSX.Element } = {
        action: (_1: CellValue, row: SearchResult) =>
            this.props.buttonForValue ? this.props.buttonForValue({ name: row.result, url: row.url }) : '',
        count: (value: CellValue) => (value === undefined ? '…' : String(value)),
        result: (_1: CellValue, row: SearchResult) => (
            <a href={row.url?.replace('/recruiter/company/', '/company/')} target="_blank">
                {row.result ?? 'Unknown Name'}
            </a>
        ),
        similarity: (value: CellValue) => Number(value).toFixed(2)
    };

    latestRequestId = 0;

    constructor(props: SimilaritySearchProps) {
        super(props);
        this.state = {
            company: undefined,
            countsError: null,
            loadingResults: false,
            searchResults: [],
            searchText: this.props.searchText,
            searchType: this.props.searchType,
            topn: 100
        };
    }

    componentDidMount() {
        if (this.props.searchText !== '') {
            this.getSearchResults(this.state.searchText, this.state.searchType, this.state.topn);
        }
    }

    componentDidUpdate(prevProps: SimilaritySearchProps) {
        if (prevProps.searchText !== this.props.searchText || prevProps.searchType !== this.props.searchType) {
            this.setState({ searchText: this.props.searchText, searchType: this.props.searchType, company: undefined });
            this.getSearchResults(this.props.searchText, this.props.searchType, this.state.topn);
        }
    }

    handleSearchTypeChange = (_: React.ChangeEvent<{}>, value: SearchType) => {
        this.latestRequestId++;
        this.setState({ searchType: value, searchText: '', searchResults: [] });
    };

    getSearchQuery = (searchText: string) => {
        const dataSource = this.props.dataSource.find((s) => s.label === searchText);
        if (dataSource) {
            return dataSource.value;
        }
        return searchText;
    };

    getResultCellValue =
        (column: ResultColumn) =>
        (row: SearchResult): CellValue => {
            switch (column) {
                case 'result':
                    return row.result;
                case 'similarity':
                    return Number(row.similarity);
                case 'count':
                    return row.count;
                default:
                    return '';
            }
        };

    saveDownloadedSearchResults = () => {
        if (this.state.searchResults && this.state.searchResults.length > 0) {
            const showSimilarity = this.state.searchType !== 'skills';
            let csvResult = showSimilarity ? 'result,similarity,number_of_profiles\n' : 'result,matching_profiles\n';
            this.state.searchResults.map((result) => {
                const columns = showSimilarity
                    ? [result.result, result.similarity, result.count]
                    : [result.result, result.count ?? ''];
                csvResult += columns.join(',') + '\n';
            });
            const blob = new Blob([csvResult], { type: 'octet/stream' });
            const date = moment();
            const dateStr = date.format('YYYY-MM-DD-hh-mm-ss');
            saveAs(blob, `${this.state.searchType} - ${this.getSearchQuery(this.state.searchText)} - ${dateStr}.csv`);
        }
    };

    handleSearch = (e: { preventDefault: () => void }) => {
        e.preventDefault();
        const { searchText, searchType, topn } = this.state;
        if (searchText && searchType && topn) {
            this.setState({ loadingResults: true, searchResults: [] });
            this.getSearchResults(searchText, searchType, topn);
        }
    };

    getSearchResults = (searchText: string, searchType: SearchType, topn: number) => {
        const searchQuery = this.getSearchQuery(searchText);
        const requestId = ++this.latestRequestId;
        this.setState({ countsError: null, loadingResults: true, searchResults: [] });
        getSimilaritySearchResults(searchQuery, searchType, topn).then((result) => {
            if (requestId !== this.latestRequestId) return;
            const searchResults: SearchResult[] = result.data;
            this.setState({ loadingResults: false, searchResults });
            if (searchType === 'skills') {
                this.loadSkillCounts(searchResults, requestId);
            }
        });
    };

    loadSkillCounts = (searchResults: SearchResult[], requestId: number) => {
        const terms = searchResults.map((r) => r.result);
        getSimilaritySkillCounts(terms, this.props.searchIn).then((result) => {
            if (requestId !== this.latestRequestId) return;
            if (!result.success) {
                const message = result.data?.message ?? result.data?.error ?? 'request failed';
                this.setState({ countsError: new Error(message) });
                return;
            }
            const counts: { [term: string]: number } = result.data;
            this.setState({ searchResults: searchResults.map((r) => ({ ...r, count: counts[r.result] })) });
        });
    };

    handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchText: e.target.value });
    };

    handleTextFieldKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            this.handleSearch({ preventDefault: () => null });
        }
    };

    handleCompanyChange = (company: { name: string; url: string }) => {
        this.setState({ company, searchText: company.url });
    };

    handleTopNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ topn: Number(e.target.value) });
    };

    handleSearchRequest = (_1: any, _2: number) => {
        this.handleSearch({ preventDefault: () => null });
    };

    render() {
        const { searchResults, searchText, topn, loadingResults, searchType, countsError } = this.state;
        const countsErrorAlert = countsError ? (
            <Alert severity="error">Could not load matching profile counts: {countsError.message}</Alert>
        ) : null;
        const { searchTypes } = this.props;
        const showSimilarity = searchType !== 'skills';

        let shouldRenderResults = false;
        let searchResultsContent = (
            <div>
                <Spinner />
            </div>
        );
        // Render the spinner only if currently loading results
        if (!loadingResults) {
            shouldRenderResults = searchResults && searchResults.length > 0;
            if (shouldRenderResults) {
                const columns: Array<DataPanelColumn<ResultColumn>> = [{ id: 'result', label: 'Result' }];
                if (showSimilarity) {
                    columns.push({ id: 'similarity', label: 'Similarity' });
                }
                columns.push({
                    id: 'count',
                    label: showSimilarity
                        ? 'Number of profiles'
                        : `Matching profiles (${searchSectionName[this.props.searchIn]})`
                });
                if (this.props.buttonForValue) {
                    columns.push({ id: 'action', label: '' });
                }
                searchResultsContent = (
                    <DataPanel
                        columns={columns}
                        data={searchResults}
                        getCellValue={this.getResultCellValue}
                        getCellRenderer={this.resultCellRenderers}
                        sortableColumns={['result', 'similarity', 'count']}
                        hidePagination={true}
                        pageSize={searchResults.length}
                    />
                );
            } else {
                searchResultsContent = (
                    <div>
                        <p>No results (yet).</p>
                    </div>
                );
            }
        }

        const actions = [
            <Button key="save-as-csv" onClick={this.saveDownloadedSearchResults} disabled={!shouldRenderResults}>
                Save as CSV
            </Button>,
            <Button key="close" onClick={this.props.onClose}>
                Close
            </Button>,
            <Button
                key="search"
                onClick={this.handleSearch}
                disabled={loadingResults || searchText?.trim().length === 0}
            >
                Search
            </Button>
        ];

        const inputField =
            searchType === 'companies' ? (
                <CompanyNameSelectField
                    selected={this.state.company}
                    onSelect={this.handleCompanyChange}
                    disabled={loadingResults}
                    variant="standard"
                    onKeyPress={this.handleTextFieldKeyPress}
                    recruiterLinkOnly={true}
                />
            ) : (
                <TextField
                    value={searchText}
                    onChange={this.handleSearchQueryChange}
                    label="Skills"
                    fullWidth={true}
                    disabled={loadingResults}
                    onKeyPress={this.handleTextFieldKeyPress}
                />
            );

        const tabs = searchTypes.map((s, i) => <Tab key={i} label={startCase(s)} value={s} />);
        // tslint:disable-next-line:no-magic-numbers
        const numResultsOptions = [5, 10, 20, 50, 100, 200, 500];
        const numResultsMenuItems = numResultsOptions.map((numResults) => (
            <MenuItem key={numResults} value={numResults}>
                {numResults}
            </MenuItem>
        ));
        const resultCountField = showSimilarity ? (
            <TextField
                label="# results"
                select={true}
                value={topn}
                onChange={this.handleTopNChange}
                disabled={loadingResults}
                className="similarity-search-topn"
            >
                {numResultsMenuItems}
            </TextField>
        ) : null;
        return (
            <Dialog css={styles} onClose={this.props.onClose} open={true} maxWidth="md">
                <DialogTitle>
                    <Tabs
                        value={searchType}
                        indicatorColor="primary"
                        onChange={this.handleSearchTypeChange}
                        variant="fullWidth"
                    >
                        {tabs}
                    </Tabs>
                </DialogTitle>
                <DialogContent className="dialog-content" dividers={true}>
                    <div className="similarity-search-form">
                        {resultCountField}
                        {inputField}
                    </div>
                    <div className="similarity-search-results">
                        {countsErrorAlert}
                        {searchResultsContent}
                    </div>
                </DialogContent>
                <DialogActions>{actions}</DialogActions>
            </Dialog>
        );
    }
}
